import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useMutation, useQuery } from '@apollo/client';
import { HOUSE_STYLE_V1, HouseStyle, validateColorUses, validateHouseStyle } from 'shared/lib/widgetCode/houseStyle';
import Button from '~/app/ui/Button';
import { S, A } from '~/app/ui/tokens';

const FIELDS = 'version fontEn fontZh colors { token value use } radiusButton radiusCard notes createdBy createdAt';

export const HOUSE_STYLE = gql`query HouseStyle { houseStyle { ${FIELDS} } }`;
const HOUSE_STYLE_VERSIONS = gql`query HouseStyleVersions { houseStyleVersions { ${FIELDS} } }`;
const SAVE_HOUSE_STYLE = gql`mutation SaveHouseStyle($input: HouseStyleInput!) { saveHouseStyle(input: $input) { ${FIELDS} } }`;

type Draft = Omit<HouseStyle, 'version' | 'createdBy' | 'createdAt'>;

const toDraft = (s: HouseStyle): Draft => ({
  fontEn: s.fontEn,
  fontZh: s.fontZh,
  colors: s.colors.map(c => ({ token: c.token, value: c.value, use: c.use })),
  radiusButton: s.radiusButton,
  radiusCard: s.radiusCard,
  notes: ''
});

// Admin chrome, so the admin kit's buttons and fields (the pill shapes in the preview are the house style itself).
const useStyles = createUseStyles({
  root: { display: 'grid', gap: 20, color: S.fg },
  meta: { fontSize: 13, color: S.muted },
  intro: { margin: 0, fontSize: 13, color: S.muted, lineHeight: 1.5, maxWidth: 720 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 24, '@media (max-width: 860px)': { gridTemplateColumns: '1fr' } },
  // alignContent: start, so a short column doesn't spread its rows to match the taller one.
  section: { display: 'grid', gap: 10, alignContent: 'start' },
  label: { fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: S.muted },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 38,
    padding: '8px 12px',
    fontSize: 13,
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    background: S.field,
    color: S.fg,
    fontFamily: 'inherit',
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` }
  },
  mono: { fontFamily: S.mono, fontSize: 12 },
  colorRow: { display: 'grid', gridTemplateColumns: '38px 120px 110px minmax(0, 1fr) 30px', gap: 8, alignItems: 'center' },
  swatch: { width: 38, height: 38, padding: 2, boxSizing: 'border-box', border: `1px solid ${S.fieldLine}`, borderRadius: A.radius, background: S.field, cursor: 'pointer' },
  start: { justifySelf: 'start' },
  pair: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  sample: { borderRadius: A.radius, overflow: 'hidden', border: `1px solid ${S.line}` },
  error: { fontSize: 13, color: S.red, lineHeight: 1.5 },
  ok: { fontSize: 13, color: S.green, fontWeight: 600 },
  versions: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 },
  version: { display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr) auto', gap: 10, alignItems: 'center', padding: '8px 10px', border: `1px solid ${S.line}`, borderRadius: A.radius, background: S.chrome, fontSize: 13 }
});

/** A small section drawn with the draft tokens, to see the house style before saving. */
const Sample: React.FC<{ draft: Draft }> = ({ draft }) => {
  const color = (token: string, fallback: string) => (draft.colors.find(c => c.token === token) || { value: fallback }).value;
  return (
    <div style={{ background: color('paper', '#f5f5f7'), color: color('ink', '#1d1d1f'), fontFamily: draft.fontEn, padding: 24 }}>
      <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: color('bronze', '#ad7c59'), marginBottom: 6 }}>Faraday Future</div>
      <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, marginBottom: 6 }}>Built for what’s next</div>
      <div style={{ fontSize: 14, color: color('graphite', '#48484c'), marginBottom: 16 }}>Secondary text sits under the heading.</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        <span style={{ background: color('night', '#111111'), color: color('white', '#ffffff'), borderRadius: draft.radiusButton, padding: '9px 18px', fontSize: 13, fontWeight: 600 }}>Reserve</span>
        <span style={{ border: `1px solid ${color('ink', '#1d1d1f')}`, borderRadius: draft.radiusButton, padding: '9px 18px', fontSize: 13, fontWeight: 600 }}>Learn more</span>
      </div>
      <div style={{ background: color('white', '#ffffff'), borderRadius: draft.radiusCard, padding: 16, border: `1px solid ${color('silver', '#d8d8dc')}` }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Card</div>
        <div style={{ fontSize: 13, color: color('gray', '#86868b') }}>Muted text in a card. 中文示例：智能出行。</div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
        {draft.colors.map(c => <span key={c.token} title={`--gx-${c.token}`} style={{ width: 22, height: 22, borderRadius: 4, background: c.value, border: `1px solid ${color('silver', '#d8d8dc')}` }} />)}
      </div>
    </div>
  );
};

const HouseStyleEditor: React.FC = () => {
  const c = useStyles();
  const { data, loading } = useQuery<{ houseStyle: HouseStyle }>(HOUSE_STYLE, { fetchPolicy: 'network-only' });
  const { data: versionsData } = useQuery<{ houseStyleVersions: HouseStyle[] }>(HOUSE_STYLE_VERSIONS, { fetchPolicy: 'network-only' });
  const [save, { loading: saving }] = useMutation(SAVE_HOUSE_STYLE, { refetchQueries: ['HouseStyle', 'HouseStyleVersions'] });
  const current = (data && data.houseStyle) || null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => { if (current && !draft) setDraft(toDraft(current)); }, [current, draft]);

  if (loading || !current || !draft) return <div className={c.meta}>Loading house style…</div>;

  const problems = [...validateHouseStyle(draft), ...validateColorUses(draft.colors)];
  const set = (patch: Partial<Draft>) => { setDraft({ ...draft, ...patch }); setMessage(null); };
  const setColor = (i: number, patch: Partial<Draft['colors'][number]>) => set({ colors: draft.colors.map((col, j) => (j === i ? { ...col, ...patch } : col)) });

  const handleSave = async () => {
    try {
      const { data: saved } = await save({ variables: { input: draft } });
      setDraft({ ...draft, notes: '' });
      setMessage({ ok: true, text: `Saved as version ${saved.saveHouseStyle.version}. New AI briefs use it now; pages get the new tokens when they are next deployed.` });
    } catch (e) {
      setMessage({ ok: false, text: (e as Error).message });
    }
  };

  return (
    <div className={c.root} data-testid="house-style-editor">
      <span className={c.meta} data-testid="house-style-version">
        Version {current.version}{current.createdBy ? ` · ${current.createdBy}` : ''}{current.createdAt ? ` · ${new Date(current.createdAt).toLocaleDateString()}` : ' · built in'}
      </span>
      <p className={c.intro}>
        The fonts, colors and corners every pasted section should use. Every AI brief carries the current version, the paste check compares new code with it, and every page gets these values as --gx-* CSS variables.
      </p>
      <div className={c.grid}>
        <div className={c.section}>
          <span className={c.label}>Fonts</span>
          <div className={c.pair}>
            <input className={`${c.input} ${c.mono}`} aria-label="English font stack" value={draft.fontEn} onChange={e => set({ fontEn: e.target.value })} />
            <input className={`${c.input} ${c.mono}`} aria-label="Chinese font stack" value={draft.fontZh} onChange={e => set({ fontZh: e.target.value })} />
          </div>
          <span className={c.label}>Colors</span>
          {draft.colors.map((col, i) => (
            <div key={i} className={c.colorRow} data-color={col.token}>
              <input type="color" className={c.swatch} aria-label={`${col.token} color`} value={/^#[0-9a-f]{6}$/i.test(col.value) ? col.value : '#000000'} onChange={e => setColor(i, { value: e.target.value })} />
              <input className={`${c.input} ${c.mono}`} aria-label="Color name" value={col.token} onChange={e => setColor(i, { token: e.target.value.toLowerCase() })} />
              <input className={`${c.input} ${c.mono}`} aria-label={`${col.token} hex value`} value={col.value} onChange={e => setColor(i, { value: e.target.value })} />
              <input className={c.input} aria-label={`${col.token} use`} placeholder="What it’s for" value={col.use} onChange={e => setColor(i, { use: e.target.value })} />
              <Button size="sm" variant="quiet" icon="close" aria-label={`Remove ${col.token}`} title={`Remove ${col.token}`} onClick={() => set({ colors: draft.colors.filter((_, j) => j !== i) })} />
            </div>
          ))}
          <Button size="sm" icon="add" className={c.start} onClick={() => set({ colors: [...draft.colors, { token: '', value: '#', use: '' }] })}>Add color</Button>
          <span className={c.label}>Corners</span>
          <div className={c.pair}>
            <label className={c.meta}>Buttons <input className={`${c.input} ${c.mono}`} value={draft.radiusButton} onChange={e => set({ radiusButton: e.target.value })} /></label>
            <label className={c.meta}>Cards <input className={`${c.input} ${c.mono}`} value={draft.radiusCard} onChange={e => set({ radiusCard: e.target.value })} /></label>
          </div>
          <span className={c.label}>What changed</span>
          <input className={c.input} placeholder="A short note for the history" value={draft.notes || ''} onChange={e => set({ notes: e.target.value })} />
          {problems.length > 0 && <div className={c.error}>{problems.join(' ')}</div>}
          {message && <div className={message.ok ? c.ok : c.error}>{message.text}</div>}
          <div className={c.actions}>
            <Button variant="primary" busy={saving} disabled={problems.length > 0} onClick={handleSave}>{saving ? 'Saving…' : 'Save as new version'}</Button>
            <Button onClick={() => set(toDraft(current))}>Reset</Button>
          </div>
        </div>
        <div className={c.section}>
          <span className={c.label}>Preview</span>
          <div className={c.sample}><Sample draft={draft} /></div>
          <span className={c.label}>History</span>
          <ul className={c.versions} data-testid="house-style-versions">
            {((versionsData && versionsData.houseStyleVersions) || [HOUSE_STYLE_V1]).map(v => (
              <li key={v.version} className={c.version}>
                <strong>v{v.version}</strong>
                <span className={c.meta}>{v.createdBy || 'built in'}{v.createdAt ? ` · ${new Date(v.createdAt).toLocaleString()}` : ''}{v.notes ? ` · ${v.notes}` : ''}</span>
                <Button size="sm" onClick={() => set({ ...toDraft(v), notes: `Back to v${v.version}` })}>Load</Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HouseStyleEditor;
